import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../logger/logger.service';
import { RotationCheckPhoto } from 'src/app/models/RotationCheckPhoto';

/** Where the session URIs live between app starts. */
const SESSION_KEY = 'rotationCheckUploadSessions';

/**
 * 8 MiB — 32 × 256 KiB. Google requires every non-final chunk to be a multiple
 * of 256 KiB. Only one chunk is ever held in memory, which is what makes "no
 * size cap" real rather than nominal.
 *
 * This is the ceiling, not the starting size. See STARTING_CHUNK_BYTES.
 */
const DEFAULT_CHUNK_BYTES = 8 * 1024 * 1024;
const CHUNK_ALIGNMENT = 256 * 1024;

/**
 * Where every upload starts, and Google's smallest legal chunk.
 *
 * Google commits nothing until a whole chunk arrives, so the chunk size is the
 * unit of permanent progress. At the 8 MiB ceiling every clip these crews record
 * fits in one chunk, which made each attempt all-or-nothing: an upload that died
 * at 95% left a committed offset of zero and the next attempt started again from
 * the beginning. On a field LTE link no attempt ever finished, so nothing ever
 * landed however long it retried. Starting small means the first quarter-megabyte
 * is banked within seconds and is never sent twice.
 */
const STARTING_CHUNK_BYTES = CHUNK_ALIGNMENT;

/** Attempts per chunk before the whole upload is left for the next cycle. */
const CHUNK_ATTEMPTS = 4;
const BACKOFF_BASE_MS = 1000;
const BACKOFF_CAP_MS = 60000;

/**
 * Deadlines. `fetch` has none of its own, and a socket that stalls without
 * closing holds the request open for as long as the operating system keeps it —
 * which parks the media pump, and the pump admits one item at a time, so every
 * other capture on the device stops with it.
 */
const API_TIMEOUT_MS = 30000;
/** A chunk gets this long to get going, plus time to move at BYTES_PER_MS. */
const CHUNK_SETUP_MS = 30000;
/** 8 bytes a millisecond is 8 KB/s. Below that the chunk is not arriving. */
const CHUNK_BYTES_PER_MS = 8;

/** How many times one upload may open a fresh session before it gives up for now. */
const MAX_SESSION_REMINTS = 2;

/**
 * The content types the server's strict resolver accepts on POST /uploads.
 * Anything outside this list is a 400, and a 400 marks the record terminally
 * errored — which is the loss this whole change exists to prevent. So the
 * client maps onto the list before sending rather than letting the server
 * refuse.
 */
const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
]);

/** Types real capture inputs report that mean one of the allowed types. */
const MIME_ALIASES: { [mime: string]: string } = {
  'image/jpg': 'image/jpeg',
  'image/pjpeg': 'image/jpeg',
  'image/x-png': 'image/png',
  'image/heic-sequence': 'image/heic',
  'image/heif-sequence': 'image/heif',
  'video/mov': 'video/quicktime',
  'video/x-quicktime': 'video/quicktime',
  'video/mpeg4': 'video/mp4',
  'video/m4v': 'video/x-m4v',
};

/** Bytes sent so far for one item. Lives in memory only — never written to IndexedDB. */
export interface RcUploadProgress {
  referenceCode: string;
  sentBytes: number;
  totalBytes: number;
}

/**
 * What one item that is being sent right now looks like to a screen.
 *
 * `sentBytes` is the offset Google itself has confirmed, not a count of what
 * the device pushed at the socket. Chunks are the unit of permanent progress
 * and the offset moves only when one is committed, so this number can never run
 * ahead of what has genuinely landed.
 *
 * `phase` separates the two things a running upload does. Opening the session
 * and asking what has already arrived takes a round trip or two before any byte
 * is committed, and during that time there is no honest percentage to show —
 * hence `percent` being null rather than zero.
 */
export interface RcUploadState {
  referenceCode: string;
  /** 'starting' until Google has committed a chunk, 'uploading' after that. */
  phase: 'starting' | 'uploading';
  sentBytes: number;
  totalBytes: number;
  /** 0–100, or null while nothing has been committed yet. */
  percent: number | null;
}

/**
 * What one upload attempt produced, in the same three-way shape the sync
 * service already uses for every other push:
 *
 *   ok       — the server has the bytes and returned the photo record.
 *   retry    — transient. Leave the record 'pending' and come back to it.
 *   terminal — the server will never accept this. Mark the record 'error'.
 */
export type RcUploadOutcome =
  | { result: 'ok'; photo: any }
  | { result: 'retry'; reason: string }
  | { result: 'terminal'; reason: string };

/** What is remembered between chunks, and across an app restart. */
interface UploadSession {
  uploadUrl: string;
  contentType: string;
  chunkSizeBytes: number;
  /** Size of the media the session was opened for. Different size, different session. */
  totalBytes: number;
  entryId: string;
  createdAt: string;
}

/**
 * Chunked, resumable upload of one captured item straight to Google Cloud
 * Storage.
 *
 * The bytes never pass through the Pinnacle API. The API opens a resumable
 * session, fixes the object's content type while doing so, and hands back a
 * session URI; this service PUTs the blob at that URI a chunk at a time and
 * then tells the API the upload is finished.
 *
 * Three things this buys, all of which the previous single-request multipart
 * POST could not do:
 *
 *  - A dropped connection resumes from the committed offset instead of
 *    starting again from zero. On a field LTE link that is the difference
 *    between a large clip arriving and never arriving.
 *  - Only one chunk is resident in memory, so clip size is not bounded by
 *    what the device can hold in one buffer.
 *  - The content type is chosen by the server when the session opens, so no
 *    file name is involved anywhere. A file name deciding the content type is
 *    what stored every previous rotation video as an image.
 *
 * Raw `fetch` rather than `HttpClient`: the HTTP interceptor forces
 * `Content-Type: application/json` on every request, which would corrupt a
 * binary chunk. The rest of the rotation-check sync uses fetch for the same
 * reason.
 *
 * Deliberately protocol-only. It knows two endpoint URLs and nothing about
 * rotation checks; the sync service owns when an upload runs and what happens
 * to the record afterwards.
 */
@Injectable({ providedIn: 'root' })
export class ResumableUploadService {
  /** Emits as chunks land. Subscribe for a progress bar; nothing depends on it. */
  readonly progress$ = new Subject<RcUploadProgress>();

  /**
   * Every item being sent right now, keyed by reference code, re-emitted as a
   * fresh map whenever one of them changes.
   *
   * A `BehaviorSubject` rather than a plain `Subject` because a thumbnail can
   * appear on screen part-way through an upload — with a plain subject it would
   * show nothing until the next chunk landed, which on a poor link is minutes.
   * A subscriber gets the current picture immediately and every change after it.
   *
   * An item that is not in this map is not moving. That is what lets a screen
   * tell "waiting its turn" apart from "transferring": the pump admits one item
   * at a time, so most of a queue is waiting rather than moving, and showing a
   * stalled bar at zero percent for the rest of it would be a lie.
   */
  readonly active$ = new BehaviorSubject<ReadonlyMap<string, RcUploadState>>(new Map());
  private activeMap = new Map<string, RcUploadState>();

  private progressMap = new Map<string, RcUploadProgress>();
  private aborters = new Map<string, AbortController>();

  /**
   * Bytes for items the device had no room to store.
   *
   * These records are written to IndexedDB without their blob, so this map is
   * the only copy until the upload finishes. It does not survive the app being
   * killed, and the sync service detects exactly that state and marks the
   * record errored rather than leaving it pending forever.
   */
  private heldBlobs = new Map<string, Blob>();

  constructor(private logger: LoggerService) {}

  // ───── in-memory bytes (the no-room path) ─────────────────────────────

  holdInMemory(referenceCode: string, blob: Blob): void {
    this.heldBlobs.set(referenceCode, blob);
  }

  held(referenceCode: string): Blob | undefined {
    return this.heldBlobs.get(referenceCode);
  }

  hasHeld(referenceCode: string): boolean {
    return this.heldBlobs.has(referenceCode);
  }

  releaseHeld(referenceCode: string): void {
    this.heldBlobs.delete(referenceCode);
  }

  // ───── progress ───────────────────────────────────────────────────────

  progressFor(referenceCode: string): RcUploadProgress | undefined {
    return this.progressMap.get(referenceCode);
  }

  /** What one item is doing right now, or undefined when it is not being sent. */
  stateFor(referenceCode: string): RcUploadState | undefined {
    return this.activeMap.get(referenceCode);
  }

  /**
   * Record where one running upload has got to and tell every subscriber.
   *
   * The map is copied before it is published so a subscriber comparing what it
   * was handed against what it holds sees a change. Handing out the live map
   * would mutate under them and an `OnPush` screen would never repaint.
   */
  private publishActive(referenceCode: string, sentBytes: number, totalBytes: number): void {
    this.activeMap.set(referenceCode, {
      referenceCode,
      phase: sentBytes > 0 ? 'uploading' : 'starting',
      sentBytes,
      totalBytes,
      percent: sentBytes > 0 && totalBytes > 0
        ? Math.min(100, Math.floor((sentBytes / totalBytes) * 100))
        : null,
    });
    this.active$.next(new Map(this.activeMap));
  }

  private clearActive(referenceCode: string): void {
    if (!this.activeMap.delete(referenceCode)) return;
    this.active$.next(new Map(this.activeMap));
  }

  // ───── cancellation ───────────────────────────────────────────────────

  /**
   * Stop an upload that is in flight. Used when the entry it belongs to is
   * deleted — a twenty-minute upload should not carry on writing an object
   * nothing will ever reference.
   */
  cancel(referenceCode: string): void {
    const controller = this.aborters.get(referenceCode);
    if (controller) {
      try { controller.abort(); } catch { /* already settled */ }
    }
  }

  /**
   * Drop everything remembered about one item: the session, the held bytes and
   * the progress entry.
   *
   * The partially written object is deliberately left alone. A resumable object
   * is not visible in the bucket until its session finalises, and Google
   * discards an abandoned session after about a week, so there is nothing for
   * the client to clean up and no reason to hand it a delete it could get wrong.
   */
  forget(referenceCode: string): void {
    this.clearSession(referenceCode);
    this.heldBlobs.delete(referenceCode);
    this.progressMap.delete(referenceCode);
    this.clearActive(referenceCode);
  }

  // ───── the upload ─────────────────────────────────────────────────────

  /**
   * Send one item and finalise it. Resumes automatically from whatever Google
   * already holds, so calling this again after a failure is the normal path
   * rather than a special case.
   */
  async upload(photo: RotationCheckPhoto, blob: Blob): Promise<RcUploadOutcome> {
    const ref = photo.referenceCode;
    if (!blob || !blob.size) {
      return { result: 'terminal', reason: 'there are no bytes to upload' };
    }
    if (!this.sid()) {
      return { result: 'retry', reason: 'no session' };
    }
    if (this.aborters.has(ref)) {
      return { result: 'retry', reason: 'an upload for this item is already running' };
    }

    const controller = new AbortController();
    this.aborters.set(ref, controller);
    // Published here rather than at the first chunk so a screen can say the item
    // is moving while the session is still being opened. Nothing is committed
    // yet, so it carries no percentage.
    this.publishActive(ref, 0, blob.size);
    try {
      let remints = 0;

      while (true) {
        let session = this.session(ref);
        let minted = false;
        // A session is tied to a byte count. If the media changed size, the old
        // session addresses different bytes and has to be replaced.
        if (!session || session.totalBytes !== blob.size) {
          this.clearSession(ref);
          const created = await this.createSession(photo, blob, controller.signal);
          if (!created.session) return created.outcome!;
          session = created.session;
          minted = true;
        }

        // Ask Google what it already has before sending anything. The committed
        // offset comes from the server, never from a local counter — except on a
        // session opened a moment ago, where the answer is known to be zero.
        // Asking anyway costs a round trip and adds one more request that can
        // fail before a single byte has been sent, which is what it kept doing.
        const probe = minted
          ? ({ state: 'offset', offset: 0 } as const)
          : await this.queryCommittedOffset(session, controller.signal);
        if (probe.state === 'retry') {
          return await this.completeIfAlreadyLanded(ref, controller.signal, {
            result: 'retry',
            reason: probe.reason,
          });
        }
        if (probe.state === 'gone') {
          if (++remints > MAX_SESSION_REMINTS) {
            return { result: 'retry', reason: 'the upload session kept expiring' };
          }
          this.clearSession(ref);
          continue;
        }

        if (probe.state === 'offset') {
          const sent = await this.sendChunks(ref, session, blob, probe.offset, controller.signal);
          if (sent === 'session-gone') {
            if (++remints > MAX_SESSION_REMINTS) {
              return { result: 'retry', reason: 'the upload session kept expiring' };
            }
            this.clearSession(ref);
            continue;
          }
          if (sent !== 'done') {
            if (sent.result === 'retry') {
              return await this.completeIfAlreadyLanded(ref, controller.signal, sent);
            }
            return sent;
          }
        }

        return await this.complete(ref, controller.signal);
      }
    } finally {
      this.aborters.delete(ref);
      this.progressMap.delete(ref);
      this.clearActive(ref);
    }
  }

  // ───── step 1: open a session ─────────────────────────────────────────

  private async createSession(
    photo: RotationCheckPhoto,
    blob: Blob,
    signal: AbortSignal
  ): Promise<{ session?: UploadSession; outcome?: RcUploadOutcome }> {
    const contentType = resolveUploadMimeType(photo, blob);
    const body = {
      referenceCode: photo.referenceCode,
      rotationCheckEntryID: photo.rotationCheckEntryID,
      photoType: photo.photoType,
      mediaType: photo.mediaType || 'photo',
      mimeType: contentType,
      sizeBytes: blob.size,
      latitude: photo.latitude ?? null,
      longitude: photo.longitude ?? null,
      createdDate: photo.createdDate,
    };

    let res: Response;
    try {
      res = await this.fetchBy(
        this.q(`${this.base}/uploads`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        signal,
        API_TIMEOUT_MS
      );
    } catch (e) {
      return { outcome: { result: 'retry', reason: `could not reach the API: ${String(e)}` } };
    }

    // The server already holds a finished session for this reference code. The
    // bytes are up; all that is missing locally is the record. Complete is
    // idempotent and returns it.
    if (res.status === 409) {
      return { outcome: await this.complete(photo.referenceCode, signal) };
    }

    const verdict = classify(res.status);
    if (verdict !== 'ok') {
      return { outcome: { result: verdict, reason: `POST /uploads returned ${res.status}` } };
    }

    const payload = await res.json().catch(() => null);
    if (!payload || !payload.uploadUrl) {
      // An API that predates these routes answers with 200 and index.html,
      // because an unmatched route falls back to the single-page app. Treating
      // that as success would mark the record synced with nothing uploaded, so
      // it is a retry: the record stays pending until the API catches up.
      return { outcome: { result: 'retry', reason: 'the API returned no upload session' } };
    }

    const session: UploadSession = {
      uploadUrl: String(payload.uploadUrl),
      contentType: payload.contentType || contentType,
      chunkSizeBytes: normaliseChunkSize(payload.chunkSizeBytes),
      totalBytes: blob.size,
      entryId: photo.rotationCheckEntryID,
      createdAt: new Date().toISOString(),
    };
    // Persisted before the first chunk goes out. If the app dies mid-chunk, the
    // next start finds the session and resumes instead of starting again.
    this.saveSession(photo.referenceCode, session);
    return { session };
  }

  // ───── step 2: ask what has already landed ────────────────────────────

  /**
   * Zero-length PUT with `Content-Range: bytes *​/{total}`. Google answers 308
   * with a `Range` header naming the last committed byte, or 200/201 if the
   * object is already complete.
   */
  private async queryCommittedOffset(
    session: UploadSession,
    signal: AbortSignal
  ): Promise<
    | { state: 'offset'; offset: number }
    | { state: 'done' }
    | { state: 'gone' }
    | { state: 'retry'; reason: string }
  > {
    let res: Response;
    try {
      res = await this.fetchBy(
        session.uploadUrl,
        {
          method: 'PUT',
          headers: { 'Content-Range': `bytes */${session.totalBytes}`, 'ngsw-bypass': 'true' },
        },
        signal,
        API_TIMEOUT_MS
      );
    } catch (e) {
      return { state: 'retry', reason: `resume query failed: ${String(e)}` };
    }

    if (res.status === 200 || res.status === 201) return { state: 'done' };
    if (res.status === 308) return { state: 'offset', offset: this.readCommittedOffset(res, 0) };
    if (res.status >= 500) return { state: 'retry', reason: `resume query returned ${res.status}` };
    // 404, 410 and anything else 4xx on the session URI mean the session is no
    // longer usable. The bytes are still here, so a fresh session is the fix.
    return { state: 'gone' };
  }

  // ───── step 3: send the bytes ─────────────────────────────────────────

  private async sendChunks(
    referenceCode: string,
    session: UploadSession,
    blob: Blob,
    startOffset: number,
    signal: AbortSignal
  ): Promise<RcUploadOutcome | 'done' | 'session-gone'> {
    let offset = Math.max(0, Math.min(startOffset, session.totalBytes));
    this.emitProgress(referenceCode, offset, session.totalBytes);

    // Start at Google's smallest legal chunk and double after each one that
    // lands, up to the size the session was opened with. A poor link banks a
    // quarter of a megabyte at a time and never loses it; a good one is at the
    // full chunk size within six requests and pays almost nothing for the climb.
    let chunkBytes = Math.min(STARTING_CHUNK_BYTES, session.chunkSizeBytes);

    while (offset < session.totalBytes) {
      const end = Math.min(offset + chunkBytes, session.totalBytes) - 1;
      // slice() is a view, not a copy — this is what keeps one chunk in memory.
      const chunk = blob.slice(offset, end + 1);

      let res: Response | null = null;
      let lastError = '';
      for (let attempt = 0; attempt < CHUNK_ATTEMPTS; attempt++) {
        if (signal.aborted) return { result: 'retry', reason: 'the upload was cancelled' };
        if (attempt > 0) await delay(backoffMs(attempt - 1));
        try {
          res = await this.fetchBy(
            session.uploadUrl,
            {
              method: 'PUT',
              headers: {
                'Content-Range': `bytes ${offset}-${end}/${session.totalBytes}`,
                'ngsw-bypass': 'true',
              },
              body: chunk,
            },
            signal,
            chunkDeadlineMs(chunk.size)
          );
        } catch (e) {
          res = null;
          lastError = String(e);
          continue;
        }
        if (res.status >= 500) {
          lastError = `chunk returned ${res.status}`;
          res = null;
          continue;
        }
        break;
      }

      if (!res) {
        return { result: 'retry', reason: lastError || 'the chunk could not be sent' };
      }
      if (res.status === 200 || res.status === 201) {
        offset = session.totalBytes;
        break;
      }
      if (res.status === 308) {
        const committed = this.readCommittedOffset(res, -1);
        if (committed < 0) {
          // The Range header is not readable. That happens when the bucket's
          // CORS configuration does not expose it, in which case a resume
          // cannot see the committed offset and restarts from zero. The upload
          // itself still progresses, so advance locally and say so once.
          this.warnOnce(
            'range-header',
            'the storage bucket is not exposing the Range header, so an interrupted upload will restart from the beginning'
          );
          offset = end + 1;
        } else {
          offset = committed > offset ? committed : end + 1;
        }
        // The chunk landed, so the link can carry one this size. Try twice as
        // much next time, up to what the session was opened with.
        chunkBytes = Math.min(chunkBytes * 2, session.chunkSizeBytes);
        this.emitProgress(referenceCode, offset, session.totalBytes);
        continue;
      }
      if (res.status === 404 || res.status === 410) return 'session-gone';
      return { result: 'terminal', reason: `chunk returned ${res.status}` };
    }

    this.emitProgress(referenceCode, session.totalBytes, session.totalBytes);
    return 'done';
  }

  // ───── step 4: finalise ───────────────────────────────────────────────

  /**
   * Last resort before giving up for this cycle: ask the server whether the
   * bytes are already there.
   *
   * Google commits a chunk before it answers, so a response lost on the way back
   * leaves the device believing an upload failed that in fact succeeded. That is
   * the normal shape of a bad mobile link, not an unusual one. Recovery is meant
   * to come from the resume query — but that is one more request on the same
   * failing link, and when it fails too the item is stuck: it can never learn the
   * object is finished, so it never finalises, and it retries the same dead
   * question every cycle for as long as the record exists. Six files sat fully
   * uploaded and permanently pending in exactly this state.
   *
   * The server does not have that problem. It reads the object out of the bucket
   * itself, so it knows the truth whatever the device believes, and complete is
   * idempotent by design. One API call settles it.
   *
   * `fallback` is the outcome that stands if the bytes are not there — the real
   * failure, preserved so the reason a screen shows is the one that mattered.
   *
   * The session is deliberately left alone on 409. A 409 here means the object is
   * absent or empty, which on this path usually means a partly sent upload rather
   * than a lost one, and its committed offset is worth keeping. This is why it
   * does not just call `complete`, which drops the session on 409 — correct when
   * the device believed it had sent everything, wrong when it knows it had not.
   */
  private async completeIfAlreadyLanded(
    referenceCode: string,
    signal: AbortSignal,
    fallback: RcUploadOutcome
  ): Promise<RcUploadOutcome> {
    let res: Response;
    try {
      res = await this.fetchBy(
        this.q(`${this.base}/uploads/${referenceCode}/complete`),
        { method: 'POST' },
        signal,
        API_TIMEOUT_MS
      );
    } catch {
      return fallback;
    }

    // Not there, or the server will never take it. Either way the original
    // failure is the one worth reporting.
    if (!res.ok) return fallback;

    const photo = await res.json().catch(() => null);
    if (!photo || !photo.referenceCode) return fallback;

    this.clearSession(referenceCode);
    return { result: 'ok', photo };
  }

  private async complete(referenceCode: string, signal: AbortSignal): Promise<RcUploadOutcome> {
    let res: Response;
    try {
      res = await this.fetchBy(
        this.q(`${this.base}/uploads/${referenceCode}/complete`),
        { method: 'POST' },
        signal,
        API_TIMEOUT_MS
      );
    } catch (e) {
      return { result: 'retry', reason: `could not reach the API: ${String(e)}` };
    }

    if (res.status === 409) {
      // The server looked in the bucket and the object is not there or is
      // empty. Our chunk loop thought otherwise, so send it again next cycle.
      this.clearSession(referenceCode);
      return { result: 'retry', reason: 'the server has not seen the bytes yet' };
    }

    const verdict = classify(res.status);
    if (verdict !== 'ok') {
      return { result: verdict, reason: `complete returned ${res.status}` };
    }

    const photo = await res.json().catch(() => null);
    if (!photo || !photo.referenceCode) {
      return { result: 'retry', reason: 'complete returned no record' };
    }

    this.clearSession(referenceCode);
    return { result: 'ok', photo };
  }

  // ───── session storage ────────────────────────────────────────────────
  //
  // Only the session URI and its shape live here. The bytes are already durable
  // in IndexedDB and are the real resume payload, and the committed offset is
  // re-queried from Google on every resume — so a stale entry here can waste a
  // round trip but cannot corrupt an upload.

  private sessions(): { [referenceCode: string]: UploadSession } {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }

  private session(referenceCode: string): UploadSession | null {
    return this.sessions()[referenceCode] || null;
  }

  private saveSession(referenceCode: string, session: UploadSession): void {
    const all = this.sessions();
    all[referenceCode] = session;
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(all));
    } catch (e) {
      // Losing the session only costs a restart from zero, so this must not
      // stop the upload that is about to run.
      this.logger.warn('RotationCheck/upload', 'could not store the upload session', {
        ref: referenceCode,
        error: String(e),
      });
    }
  }

  private clearSession(referenceCode: string): void {
    const all = this.sessions();
    if (!(referenceCode in all)) return;
    delete all[referenceCode];
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(all));
    } catch { /* nothing useful to do */ }
  }

  // ───── small helpers ──────────────────────────────────────────────────

  private get base(): string {
    return `${environment.apiServer}/api/rotation-check`;
  }

  private sid(): string {
    return localStorage.getItem('currentSessionID') || '';
  }

  private q(url: string): string {
    return `${url}?sid=${encodeURIComponent(this.sid())}`;
  }

  /**
   * One request, with a deadline, and with the upload's own cancellation still
   * in force.
   *
   * `fetch` has no timeout. A socket that stalls without closing keeps the
   * promise unsettled for as long as the operating system holds the connection,
   * and the media pump runs one item at a time and only re-enters when the
   * previous pass has finished — so a single stalled request stops every upload
   * on the device until the app is restarted.
   *
   * The deadline gets its own controller so that firing it cannot cancel the
   * upload as a whole; an expired request is one failed attempt, and the caller
   * retries it. The parent signal is chained onto that controller so a real
   * cancellation still reaches the request in flight.
   */
  private async fetchBy(
    url: string,
    init: RequestInit,
    signal: AbortSignal,
    timeoutMs: number
  ): Promise<Response> {
    const controller = new AbortController();
    const relay = () => controller.abort();
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', relay);

    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', relay);
    }
  }

  /**
   * Committed byte count from a `Range: bytes=0-N` response header, which is
   * N+1. Returns `missing` when the header is absent or unreadable.
   */
  private readCommittedOffset(res: Response, missing: number): number {
    const range = res.headers.get('Range') || res.headers.get('range');
    if (!range) return missing;
    const m = /bytes\s*=\s*\d+\s*-\s*(\d+)/i.exec(range);
    if (!m) return missing;
    const end = Number(m[1]);
    return Number.isFinite(end) ? end + 1 : missing;
  }

  private emitProgress(referenceCode: string, sentBytes: number, totalBytes: number): void {
    const p: RcUploadProgress = { referenceCode, sentBytes, totalBytes };
    this.progressMap.set(referenceCode, p);
    this.progress$.next(p);
    this.publishActive(referenceCode, sentBytes, totalBytes);
  }

  private warned = new Set<string>();
  private warnOnce(key: string, message: string): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.logger.warn('RotationCheck/upload', message, {});
  }
}

// ───── module-level pure helpers ────────────────────────────────────────

/**
 * The content type to open the session with.
 *
 * A file name is never consulted. The capture input's reported type is mapped
 * onto the list the server accepts, and anything unrecognised falls back to the
 * ordinary type for its media kind rather than being refused — a best-guess
 * content type is repairable, a refused upload loses the recording.
 */
export function resolveUploadMimeType(photo: RotationCheckPhoto, blob: Blob): string {
  const declared = (photo.mimeType || blob.type || '').toLowerCase().split(';')[0].trim();
  const isVideo = photo.mediaType === 'video' || declared.startsWith('video/');
  const mapped = MIME_ALIASES[declared] || declared;

  if (ALLOWED_MIME.has(mapped)) {
    // Guard the family too: a type that disagrees with the record's media kind
    // would be refused by the server's cross-check.
    const mappedIsVideo = mapped.startsWith('video/');
    if (mappedIsVideo === isVideo) return mapped;
  }
  return isVideo ? 'video/mp4' : 'image/jpeg';
}

/** Every non-final chunk must be a multiple of 256 KiB, so an odd value is refused. */
function normaliseChunkSize(value: any): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n % CHUNK_ALIGNMENT !== 0) return DEFAULT_CHUNK_BYTES;
  return n;
}

/**
 * Same three-way split the rotation-check sync uses everywhere else: auth,
 * server errors, timeouts and throttling are worth retrying; other client
 * errors are not.
 */
function classify(status: number): 'ok' | 'retry' | 'terminal' {
  if (status >= 200 && status < 300) return 'ok';
  if (status === 401 || status === 403 || status === 408 || status === 425 || status === 429 || status >= 500) {
    return 'retry';
  }
  return 'terminal';
}

/**
 * How long one chunk gets before the attempt is abandoned. Enough to open the
 * connection, plus enough to carry the bytes at a rate below which they were
 * never going to arrive.
 */
function chunkDeadlineMs(bytes: number): number {
  return CHUNK_SETUP_MS + Math.ceil(bytes / CHUNK_BYTES_PER_MS);
}

/** 1s, 2s, 4s, 8s … capped at 60s, halved and re-spread so retries do not align. */
function backoffMs(step: number): number {
  const ceiling = Math.min(BACKOFF_BASE_MS * Math.pow(2, step), BACKOFF_CAP_MS);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
